import { useEffect, useMemo, useState } from "react";
import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../contexts/AuthContext";
import Modal from "../ui/Modal";
import ConfirmDialog from "../ui/ConfirmDialog";
import FormField, { FormActions, fieldInputStyle } from "../ui/FormField";
import {
  createFinanceiro,
  updateFinanceiro,
  deleteFinanceiro,
  getFinanceiro,
} from "../../services/financeiroService";
import { listClientes } from "../../services/clientesService";
import {
  listFornecedores,
  tipoFornecedorIcon,
} from "../../services/fornecedoresService";
import FornecedorFormModal from "./FornecedorFormModal";
import TaxonomyManagerModal from "./TaxonomyManagerModal";
import {
  updateParcela,
  marcarPaga,
  desmarcarPaga,
} from "../../services/parcelasService";
import * as categoriasService from "../../services/categoriasService";
import * as formasPagamentoService from "../../services/formasPagamentoService";
import { filtrarPorTipoFinanceiro } from "../../services/categoriasService";

// Labels de status variam por tipo (enum backend inalterado — cosmético na UI).
const STATUS_OPTIONS_DESPESA = [
  { value: 0, label: "Pendente" },
  { value: 1, label: "Pago parcial" },
  { value: 2, label: "Pago" },
];
const STATUS_OPTIONS_RECEITA = [
  { value: 0, label: "A receber" },
  { value: 1, label: "Recebido parcial" },
  { value: 2, label: "Recebido" },
];
// Categoria e FormaPagamento agora vêm da API (PR2b) — listas customizáveis
// por tenant via /api/Categorias e /api/FormasPagamento, com botão
// "+ Gerenciar" inline que abre TaxonomyManagerModal pra CRUD.

// TipoFinanceiro: 0 Receita · 1 Despesa
const TIPO_RECEITA = 0;
const TIPO_DESPESA = 1;

const EMPTY_DESPESA = {
  valor: "",
  // Fase 2 (moeda-por-guia): moeda saiu do form — herda do guia logado.
  // PR2b: agora IDs (int). Backend aceita string legado tb, mas frontend
  // novo envia só ID. Vazio = "(Selecione...)".
  formaPagamentoId: "",
  status: 0,
  tipo: TIPO_DESPESA,
  categoriaId: "",
  descricao: "",
  vencimento: "",
  clienteId: "",
  fornecedorId: "",
  // Parcelamento — só usado em criação. 1 = à vista (comportamento original).
  numeroParcelas: 1,
  primeiraParcelaVencimento: "",
  intervaloParcelasDias: 30,
};

const EMPTY_RECEITA = {
  ...EMPTY_DESPESA,
  tipo: TIPO_RECEITA,
};

function symbol(moeda) {
  return moeda === "EUR" ? "€" : moeda === "BRL" ? "R$" : moeda === "CHF" ? "CHF" : "$";
}

function fmt(v) {
  return Number(v ?? 0).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export default function FinanceiroFormModal({
  open,
  lancamento,
  // "receita" | "despesa" — usado só em criação. Default "despesa" se omitido.
  tipoInicial,
  onClose,
  onSaved,
  onDeleted,
}) {
  const { C } = useTheme();
  const { profile } = useAuth();
  const isEdit = !!lancamento;
  // Fase 2: símbolo do display vem do lançamento (histórico, ao editar) ou do
  // guia logado (na criação). Lançamento já tem moeda gravada — preserva o
  // histórico mesmo se a moeda do guia mudasse no futuro.
  const moedaExibicao = lancamento?.moeda ?? profile?.moeda ?? "EUR";
  const [form, setForm] = useState(EMPTY_DESPESA);
  const [clientes, setClientes] = useState([]);
  const [fornecedores, setFornecedores] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [formasPagamento, setFormasPagamento] = useState([]);
  const [parcelas, setParcelas] = useState([]);
  const [parcelasDirty, setParcelasDirty] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [fornecedorModalOpen, setFornecedorModalOpen] = useState(false);
  const [categoriaManagerOpen, setCategoriaManagerOpen] = useState(false);
  const [formaManagerOpen, setFormaManagerOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    listClientes()
      .then(setClientes)
      .catch(() => setClientes([]));
    listFornecedores()
      .then(setFornecedores)
      .catch(() => setFornecedores([]));
    // Taxonomia (PR2b) — primeira chamada dispara lazy-seed das categorias/
    // formas de sistema no backend pra tenants novos.
    categoriasService.listCategorias()
      .then(setCategorias)
      .catch(() => setCategorias([]));
    formasPagamentoService.listFormasPagamento()
      .then(setFormasPagamento)
      .catch(() => setFormasPagamento([]));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (lancamento) {
      setForm({
        valor: lancamento.valor != null ? String(lancamento.valor) : "",
        // PR2b: usa o ID que vem do DTO (resolvido pelo backend com FK > Legado).
        formaPagamentoId:
          lancamento.formaPagamentoId != null ? String(lancamento.formaPagamentoId) : "",
        status: lancamento.status ?? 0,
        tipo: lancamento.tipo ?? TIPO_DESPESA,
        categoriaId:
          lancamento.categoriaId != null ? String(lancamento.categoriaId) : "",
        descricao: lancamento.descricao ?? "",
        vencimento: lancamento.vencimento ?? "",
        clienteId:
          lancamento.clienteId != null ? String(lancamento.clienteId) : "",
        fornecedorId:
          lancamento.fornecedorId != null ? String(lancamento.fornecedorId) : "",
        // Parcelamento — só relevante em criação, mas mantém o default no edit.
        numeroParcelas: 1,
        primeiraParcelaVencimento: "",
        intervaloParcelasDias: 30,
      });
      setParcelas(lancamento.parcelas ?? []);
    } else {
      // Criação — tipo determinado por tipoInicial (default despesa).
      setForm(tipoInicial === "receita" ? EMPTY_RECEITA : EMPTY_DESPESA);
      setParcelas([]);
    }
    setParcelasDirty(new Set());
    setError(null);
  }, [open, lancamento, tipoInicial]);

  const somaParcelas = useMemo(
    () => parcelas.reduce((a, p) => a + (Number(p.valor) || 0), 0),
    [parcelas]
  );
  const valorForm = parseFloat(String(form.valor).replace(",", ".")) || 0;
  const somaBate = Math.abs(somaParcelas - valorForm) < 0.01;

  // À-vista puro = exatamente 1 parcela não-entrada e sem entrada. Essa parcela
  // espelha a mãe (o backend a sincroniza no Put — Fase C2.1), então é detalhe
  // de implementação: NÃO mostramos a seção "Parcelas (1)" redundante, que ainda
  // dispararia o aviso "soma não bate" ao editar só o Valor do topo. A seção só
  // aparece com parcelamento real (>=2) ou quando há entrada (receita de roteiro).
  const temEntrada = parcelas.some((p) => p.ehEntrada);
  const parcelasNaoEntrada = parcelas.filter((p) => !p.ehEntrada);
  const isAVistaPuro = !temEntrada && parcelasNaoEntrada.length === 1;
  const mostrarSecaoParcelas = isEdit && parcelas.length > 0 && !isAVistaPuro;
  // Derivações baseadas em form.tipo (reflete seleção atual, não só edição).
  const isReceita = Number(form.tipo) === TIPO_RECEITA;
  const isDespesa = Number(form.tipo) === TIPO_DESPESA;
  // Receita de roteiro = gerada automaticamente, não tem categoria editável.
  const isRoteiroLinked = !!lancamento?.roteiroId;
  // Despesa originada de tarefa do roteiro — mostra aviso + link (commit 5)
  const isTarefaLinked = !!lancamento?.tarefaRoteiroId;

  // Parcelamento — só ativa em criação. Edição usa o fluxo existente (ajustar
  // valor/vencimento por parcela direto na tabela já renderizada abaixo).
  const numeroParcelasNum = parseInt(form.numeroParcelas, 10) || 1;
  const parcelar = !isEdit && numeroParcelasNum >= 2;

  // Preview dos valores das parcelas — replica o arredondamento do backend
  // (Math.Round AwayFromZero a 2 casas; última absorve o resíduo). Pra valores
  // positivos JS Math.round = AwayFromZero, então bate.
  const previewParcelas = useMemo(() => {
    if (!parcelar) return null;
    const v = parseFloat(String(form.valor).replace(",", ".")) || 0;
    if (v <= 0) return null;
    const base = Math.round((v / numeroParcelasNum) * 100) / 100;
    const ultima = Math.round((v - base * (numeroParcelasNum - 1)) * 100) / 100;
    return { base, ultima, ajustada: Math.abs(base - ultima) >= 0.005 };
  }, [parcelar, form.valor, numeroParcelasNum]);

  function update(key, value) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function updateParcelaLocal(id, patch) {
    setParcelas((ps) =>
      ps.map((p) => (p.id === id ? { ...p, ...patch } : p))
    );
    setParcelasDirty((d) => new Set(d).add(id));
  }

  function close() {
    if (saving || deleting) return;
    onClose?.();
  }

  async function savePendingParcelas(financeiroId) {
    if (parcelasDirty.size === 0) return;
    const errors = [];
    for (const p of parcelas) {
      if (!parcelasDirty.has(p.id)) continue;
      try {
        await updateParcela(financeiroId, p.id, {
          valor: Number(p.valor) || 0,
          vencimento: p.vencimento,
          pagoEm: p.pagoEm || null,
          pago: !!p.pago,
        });
      } catch (err) {
        errors.push(`Parcela ${p.id}: ${err?.response?.data?.message || "erro"}`);
      }
    }
    if (errors.length > 0) throw new Error(errors.join(" · "));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const valor = parseFloat(String(form.valor).replace(",", "."));
    if (!Number.isFinite(valor) || valor <= 0) {
      setError("Informe um valor válido maior que zero.");
      return;
    }
// Categoria obrigatória em despesa e em receita avulsa (não em receita de roteiro).
    if (!isRoteiroLinked && !form.categoriaId) {
      setError(
        isReceita
          ? "Selecione uma categoria para a receita."
          : "Selecione uma categoria para a despesa."
      );
      return;
    }

    // Parcelamento (só em criação) — valida campos obrigatórios.
    if (parcelar) {
      if (numeroParcelasNum > 60) {
        setError("Número de parcelas deve estar entre 1 e 60.");
        return;
      }
      if (!form.primeiraParcelaVencimento) {
        setError("Informe a data da 1ª parcela.");
        return;
      }
    }

    setSaving(true);
    setError(null);
    try {
      const payload = {
        valor,
        // PR2b: envia IDs (int). Backend prefere ID quando ambos vêm; mantemos
        // só o ID pra simplificar o contrato. Vazio = null = sem forma.
        formaPagamentoId: form.formaPagamentoId ? Number(form.formaPagamentoId) : null,
        // Quando parcelado, o backend força Pendente (e a UI já avisa) — mas
        // mandamos o valor selecionado mesmo assim; quem decide é o servidor.
        status: Number(form.status),
        tipo: Number(form.tipo),
        // Receita de roteiro não carrega categoria editável; resto envia o ID.
        categoriaId: isRoteiroLinked ? null : (form.categoriaId ? Number(form.categoriaId) : null),
        descricao: form.descricao.trim() || null,
        // Se parcelado, Vencimento é derivado da 1ª parcela no backend — manda
        // null pra evitar duplicação semântica.
        vencimento: parcelar ? null : (form.vencimento || null),
        clienteId: form.clienteId ? Number(form.clienteId) : null,
        // Fornecedor só faz sentido em despesa — receitas enviam null
        fornecedorId: isDespesa && form.fornecedorId ? Number(form.fornecedorId) : null,
        // Parcelamento — só em criação e quando N >= 2.
        ...(parcelar && {
          numeroParcelas: numeroParcelasNum,
          primeiraParcelaVencimento: form.primeiraParcelaVencimento,
          intervaloParcelasDias: parseInt(form.intervaloParcelasDias, 10) || 30,
        }),
      };
      if (isEdit) {
        await updateFinanceiro(lancamento.id, payload);
        await savePendingParcelas(lancamento.id);
      } else {
        // Criação — sempre Despesa (a UI trava o Tipo)
        await createFinanceiro(payload);
      }
      onSaved?.();
      onClose?.();
    } catch (err) {
      setError(
        err?.message ||
          err?.response?.data?.message ||
          err?.response?.data?.errors?.join?.(" ") ||
          "Erro ao salvar lançamento."
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!isEdit) return;
    setDeleting(true);
    setError(null);
    try {
      await deleteFinanceiro(lancamento.id);
      setConfirmDelete(false);
      onDeleted?.();
      onClose?.();
    } catch (err) {
      setError(err?.response?.data?.message || "Erro ao excluir lançamento.");
      setConfirmDelete(false);
    } finally {
      setDeleting(false);
    }
  }

  async function toggleParcelaPaga(parcela) {
    if (!isEdit) return;
    try {
      if (parcela.pago) {
        await desmarcarPaga(lancamento.id, parcela);
      } else {
        await marcarPaga(lancamento.id, parcela);
      }
      const fresh = await getFinanceiro(lancamento.id);
      setParcelas(fresh.parcelas ?? []);
      setParcelasDirty(new Set());
      onSaved?.();
    } catch (err) {
      setError(err?.response?.data?.message || "Erro ao atualizar parcela.");
    }
  }

  const inputStyle = fieldInputStyle(C);
  const sectionLabelStyle = {
    fontSize: 11,
    fontWeight: 700,
    color: C.accent,
    textTransform: "uppercase",
    letterSpacing: 1,
    marginTop: 20,
    marginBottom: 10,
    paddingBottom: 6,
    borderBottom: `1px solid ${C.border}`,
  };

  // Título dinâmico
  const modalTitle = !isEdit
    ? (isReceita ? "Nova Receita" : "Nova Despesa")
    : (isReceita ? "Editar Receita" : "Editar Despesa");
  const modalSubtitle = !isEdit
    ? (isReceita
        ? "Registre uma receita avulsa (fora de roteiros)"
        : "Registre uma despesa operacional")
    : lancamento?.roteiroTitulo
    ? `Roteiro: ${lancamento.roteiroTitulo}`
    : lancamento?.descricao || lancamento?.clienteNome || "Lançamento financeiro";

  return (
    <>
      <Modal
        open={open}
        onClose={close}
        title={modalTitle}
        subtitle={modalSubtitle}
        width={680}
      >
        <form onSubmit={handleSubmit}>
          {/* Aviso quando editando receita gerada por roteiro */}
          {isReceita && lancamento?.roteiroId && (
            <div
              style={{
                marginBottom: 16,
                padding: "10px 14px",
                background: C.accentDim,
                borderLeft: `3px solid ${C.accent}`,
                borderRadius: 8,
                fontSize: 11,
                color: C.accent,
              }}
            >
              💡 Esta receita foi gerada automaticamente pelo roteiro. Valores e
              parcelas podem ser ajustados aqui — se alterar o roteiro e reconfirmar,
              as parcelas serão regeradas.
            </div>
          )}

          {/* Aviso quando editando despesa originada de tarefa */}
          {isDespesa && isTarefaLinked && (
            <div
              style={{
                marginBottom: 16,
                padding: "10px 14px",
                background: C.accentDim,
                borderLeft: `3px solid ${C.accent}`,
                borderRadius: 8,
                fontSize: 11,
                color: C.accent,
              }}
            >
              🔗 Originada da tarefa{" "}
              <strong>"{lancamento.tarefaRoteiroTitulo}"</strong>
              {lancamento.tarefaRoteiroRoteiroTitulo && (
                <> do roteiro <strong>"{lancamento.tarefaRoteiroRoteiroTitulo}"</strong></>
              )}
              . Editar valor/fornecedor/categoria aqui NÃO atualiza a tarefa —
              para manter em sincronia, altere diretamente pela tarefa do roteiro.
            </div>
          )}

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 14,
            }}
          >
            {/* Tipo — oculto na criação (travado em Despesa); read-only na edição */}
            {isEdit && (
              <FormField label="Tipo">
                <input
                  value={isReceita ? "Receita" : "Despesa"}
                  readOnly
                  tabIndex={-1}
                  style={{
                    ...inputStyle,
                    opacity: 0.7,
                    cursor: "default",
                    color: isReceita ? C.green : C.red,
                    fontWeight: 600,
                  }}
                />
              </FormField>
            )}

            <FormField label="Status" span={isEdit ? 1 : 2}>
              <select
                value={form.status}
                onChange={(e) => update("status", e.target.value)}
                style={inputStyle}
              >
                {(isReceita ? STATUS_OPTIONS_RECEITA : STATUS_OPTIONS_DESPESA).map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </FormField>

            <FormField label="Valor" required>
              <input
                type="number"
                step="0.01"
                min="0"
                value={form.valor}
                onChange={(e) => update("valor", e.target.value)}
                style={inputStyle}
                placeholder="0,00"
                autoFocus
              />
            </FormField>

            {/* Fase 2: campo Moeda removido — herda do guia logado. */}

            {/* Categoria — visível em despesa e em receita avulsa.
                Receita de roteiro não tem categoria (o roteiro é a origem).
                Lista filtrada por escopo (Receita ↔ Despesa, Ambas em ambas).
                Fallback: se categoriaId atual não casa com filtro (caso raro
                — categoria mudou de escopo depois do lançamento criado),
                injeta a categoria atual no dropdown pra não ficar em branco. */}
            {(isDespesa || (isReceita && !isRoteiroLinked)) && (() => {
              const filtradas = filtrarPorTipoFinanceiro(categorias, Number(form.tipo));
              const atualId = form.categoriaId ? Number(form.categoriaId) : null;
              const atualNoFiltro = atualId != null && filtradas.some((c) => c.id === atualId);
              const atual = atualId != null && !atualNoFiltro
                ? categorias.find((c) => c.id === atualId)
                : null;
              const options = atual ? [...filtradas, atual] : filtradas;
              return (
                <FormField label="Categoria" required span={2}>
                  <div style={{ display: "flex", gap: 8 }}>
                    <select
                      value={form.categoriaId}
                      onChange={(e) => update("categoriaId", e.target.value)}
                      style={{ ...inputStyle, flex: 1 }}
                    >
                      <option value="">Selecione...</option>
                      {options.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.nome}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => setCategoriaManagerOpen(true)}
                      title="Gerenciar categorias"
                      style={{
                        padding: "11px 14px",
                        borderRadius: 10,
                        border: `1px solid ${C.accent}`,
                        background: "transparent",
                        color: C.accent,
                        fontSize: 12,
                        fontWeight: 700,
                        cursor: "pointer",
                        fontFamily: "'DM Sans', sans-serif",
                        whiteSpace: "nowrap",
                      }}
                    >
                      + Gerenciar
                    </button>
                  </div>
                </FormField>
              );
            })()}

            <FormField label="Forma de Pagamento" span={2}>
              <div style={{ display: "flex", gap: 8 }}>
                <select
                  value={form.formaPagamentoId}
                  onChange={(e) => update("formaPagamentoId", e.target.value)}
                  style={{ ...inputStyle, flex: 1 }}
                >
                  <option value="">Selecione...</option>
                  {formasPagamento.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.nome}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setFormaManagerOpen(true)}
                  title="Gerenciar formas de pagamento"
                  style={{
                    padding: "11px 14px",
                    borderRadius: 10,
                    border: `1px solid ${C.accent}`,
                    background: "transparent",
                    color: C.accent,
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: "pointer",
                    fontFamily: "'DM Sans', sans-serif",
                    whiteSpace: "nowrap",
                  }}
                >
                  + Gerenciar
                </button>
              </div>
            </FormField>

            {/* Vencimento — escondido quando parcelando na criação (vira a 1ª
                parcela, evita campo duplicado). Em edição sempre aparece. */}
            {!parcelar && (
              <FormField label="Vencimento">
                <input
                  type="date"
                  value={form.vencimento}
                  onChange={(e) => update("vencimento", e.target.value)}
                  style={inputStyle}
                />
              </FormField>
            )}

            {/* Parcelar em — só em criação. Default 1 = à vista (campos extras
                escondidos). 2..60 = revela "1ª parcela" + "Intervalo". */}
            {!isEdit && (
              <FormField
                label="Parcelar em"
                hint={parcelar ? `${numeroParcelasNum}x` : "1 = à vista"}
                span={parcelar ? 1 : 2}
              >
                <input
                  type="number"
                  min="1"
                  max="60"
                  value={form.numeroParcelas}
                  onChange={(e) => update("numeroParcelas", e.target.value)}
                  style={inputStyle}
                />
              </FormField>
            )}

            {parcelar && (
              <>
                <FormField label="1ª parcela" required>
                  <input
                    type="date"
                    value={form.primeiraParcelaVencimento}
                    onChange={(e) =>
                      update("primeiraParcelaVencimento", e.target.value)
                    }
                    style={inputStyle}
                  />
                </FormField>

                <FormField
                  label="Intervalo (dias)"
                  hint="Padrão: 30 (mensal)"
                  span={2}
                >
                  <input
                    type="number"
                    min="1"
                    value={form.intervaloParcelasDias}
                    onChange={(e) =>
                      update("intervaloParcelasDias", e.target.value)
                    }
                    style={inputStyle}
                  />
                </FormField>

                {previewParcelas && (
                  <div
                    style={{
                      gridColumn: "1 / -1",
                      padding: "10px 14px",
                      background: C.bg,
                      border: `1px solid ${C.border}`,
                      borderRadius: 8,
                      fontSize: 12,
                      color: C.text,
                    }}
                  >
                    <strong>
                      {numeroParcelasNum}x de {symbol(moedaExibicao)}{" "}
                      {fmt(previewParcelas.base)}
                    </strong>
                    {previewParcelas.ajustada && (
                      <span style={{ color: C.textMuted }}>
                        {" "}
                        · última de {symbol(moedaExibicao)}{" "}
                        {fmt(previewParcelas.ultima)} (ajuste de arredondamento)
                      </span>
                    )}
                  </div>
                )}

                <div
                  style={{
                    gridColumn: "1 / -1",
                    padding: "10px 14px",
                    background: C.accentDim,
                    borderLeft: `3px solid ${C.accent}`,
                    borderRadius: 8,
                    fontSize: 11,
                    color: C.accent,
                  }}
                >
                  💡 Status fica "
                  {isReceita ? "A receber" : "Pendente"}" até as parcelas serem
                  marcadas como pagas individualmente. Depois de criar, abra o
                  lançamento pra ajustar valores/datas ou marcar parcelas como
                  pagas.
                </div>
              </>
            )}

            <FormField label="Cliente" span={2}>
              <select
                value={form.clienteId}
                onChange={(e) => update("clienteId", e.target.value)}
                style={inputStyle}
              >
                <option value="">(Sem cliente)</option>
                {clientes.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nome}
                  </option>
                ))}
              </select>
            </FormField>

            {/* Fornecedor — só em despesa. Botão "+ Novo" abre modal inline. */}
            {isDespesa && (
              <FormField label="Fornecedor" span={2}>
                <div style={{ display: "flex", gap: 8 }}>
                  <select
                    value={form.fornecedorId}
                    onChange={(e) => update("fornecedorId", e.target.value)}
                    style={{ ...inputStyle, flex: 1 }}
                  >
                    <option value="">(Sem fornecedor)</option>
                    {fornecedores.map((f) => (
                      <option key={f.id} value={f.id}>
                        {tipoFornecedorIcon(f.tipo)} {f.nome}
                        {f.cidade ? ` — ${f.cidade}` : ""}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => setFornecedorModalOpen(true)}
                    title="Criar novo fornecedor"
                    style={{
                      padding: "11px 14px",
                      borderRadius: 10,
                      border: `1px solid ${C.accent}`,
                      background: "transparent",
                      color: C.accent,
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: "pointer",
                      fontFamily: "'DM Sans', sans-serif",
                      whiteSpace: "nowrap",
                    }}
                  >
                    + Novo
                  </button>
                </div>
              </FormField>
            )}

            <FormField label="Descrição" span={2}>
              <textarea
                value={form.descricao}
                onChange={(e) => update("descricao", e.target.value)}
                rows={2}
                style={{ ...inputStyle, resize: "vertical", fontFamily: "'DM Sans', sans-serif" }}
                placeholder={
                  isReceita && !isRoteiroLinked
                    ? "Ex: Venda de bilhete Bernina com margem"
                    : "Detalhes do lançamento..."
                }
              />
            </FormField>
          </div>

          {/* Parcelas — só parcelado real (>=2) ou com entrada. À-vista puro
              (1 parcela espelho) esconde a seção: o Valor/Vencimento do topo é
              a fonte e o backend sincroniza a parcela única (Fase C2.1). */}
          {mostrarSecaoParcelas && (
            <>
              <div style={sectionLabelStyle}>Parcelas ({parcelas.length})</div>

              {!somaBate && (
                <div
                  style={{
                    marginBottom: 10,
                    padding: "8px 12px",
                    background: C.accentDim,
                    borderLeft: `3px solid ${C.accent}`,
                    borderRadius: 6,
                    fontSize: 11,
                    color: C.accent,
                    fontWeight: 500,
                  }}
                >
                  ⚠️ A soma das parcelas ({symbol(moedaExibicao)} {fmt(somaParcelas)}) não
                  bate com o valor total ({symbol(moedaExibicao)} {fmt(valorForm)}).
                  Diferença: {symbol(moedaExibicao)} {fmt(Math.abs(somaParcelas - valorForm))}.
                </div>
              )}

              <div
                style={{
                  border: `1px solid ${C.border}`,
                  borderRadius: 10,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "40px 1fr 1fr 100px 80px",
                    padding: "8px 12px",
                    background: C.bg,
                    borderBottom: `1px solid ${C.border}`,
                    fontSize: 10,
                    fontWeight: 700,
                    color: C.textDim,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    gap: 8,
                  }}
                >
                  <span>#</span>
                  <span>Valor</span>
                  <span>Vencimento</span>
                  <span>Status</span>
                  <span></span>
                </div>
                {parcelas.map((p, idx) => (
                  <div
                    key={p.id}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "40px 1fr 1fr 100px 80px",
                      padding: "8px 12px",
                      borderBottom:
                        idx < parcelas.length - 1
                          ? `1px solid ${C.border}`
                          : "none",
                      alignItems: "center",
                      gap: 8,
                      background: p.pago ? C.greenDim : "transparent",
                    }}
                  >
                    <span style={{ fontSize: 12, color: C.textMuted, fontWeight: 600 }}>
                      {idx + 1}
                    </span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={p.valor}
                      disabled={p.pago}
                      onChange={(e) =>
                        updateParcelaLocal(p.id, {
                          valor: parseFloat(e.target.value) || 0,
                        })
                      }
                      style={{
                        ...inputStyle,
                        padding: "7px 10px",
                        fontSize: 12,
                        opacity: p.pago ? 0.6 : 1,
                      }}
                    />
                    <input
                      type="date"
                      value={p.vencimento}
                      disabled={p.pago}
                      onChange={(e) =>
                        updateParcelaLocal(p.id, { vencimento: e.target.value })
                      }
                      style={{
                        ...inputStyle,
                        padding: "7px 10px",
                        fontSize: 12,
                        opacity: p.pago ? 0.6 : 1,
                      }}
                    />
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: p.pago ? C.green : C.textMuted,
                      }}
                    >
                      {p.pago
                        ? `Pago ${p.pagoEm ? p.pagoEm.split("-").reverse().join("/") : ""}`
                        : "Pendente"}
                    </span>
                    <button
                      type="button"
                      onClick={() => toggleParcelaPaga(p)}
                      style={{
                        padding: "6px 10px",
                        borderRadius: 6,
                        border: `1px solid ${p.pago ? C.textMuted : C.green}`,
                        background: "transparent",
                        color: p.pago ? C.textMuted : C.green,
                        fontSize: 10,
                        fontWeight: 700,
                        cursor: "pointer",
                        fontFamily: "'DM Sans', sans-serif",
                      }}
                    >
                      {p.pago ? "Desfazer" : "✓ Pago"}
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}

          {error && (
            <div
              style={{
                marginTop: 14,
                padding: "10px 14px",
                background: C.redDim,
                color: C.red,
                borderRadius: 8,
                fontSize: 12,
                fontWeight: 500,
              }}
            >
              {error}
            </div>
          )}

          <FormActions
            onCancel={close}
            onDelete={isEdit ? () => setConfirmDelete(true) : undefined}
            saving={saving}
            submitLabel={
              !isEdit
                ? (isReceita ? "Criar receita" : "Criar despesa")
                : "Salvar alterações"
            }
            submitBackground={isReceita ? C.green : undefined}
          />
        </form>
      </Modal>

      <ConfirmDialog
        open={confirmDelete}
        title={isReceita ? "Excluir receita?" : "Excluir despesa?"}
        message={`Tem certeza que deseja excluir este lançamento${
          lancamento?.clienteNome ? ` de ${lancamento.clienteNome}` : ""
        }? As parcelas vinculadas também serão removidas.`}
        confirmLabel="Excluir"
        danger
        loading={deleting}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={handleDelete}
      />

      {/* Modal inline de novo fornecedor — ao criar, pré-seleciona no dropdown */}
      <FornecedorFormModal
        open={fornecedorModalOpen}
        fornecedor={null}
        onClose={() => setFornecedorModalOpen(false)}
        onCreated={(created) => {
          setFornecedores((curr) => [...curr, created].sort((a, b) => a.nome.localeCompare(b.nome)));
          setForm((f) => ({ ...f, fornecedorId: String(created.id) }));
        }}
      />

      {/* Gerenciador de Categorias — CRUD inline + pré-seleciona ao criar */}
      <TaxonomyManagerModal
        open={categoriaManagerOpen}
        onClose={() => setCategoriaManagerOpen(false)}
        onList={categoriasService.listCategorias}
        onCreate={categoriasService.createCategoria}
        onUpdate={categoriasService.updateCategoria}
        onDelete={categoriasService.deleteCategoria}
        titulo="Gerenciar Categorias"
        itemLabel="categoria"
        hasEscopo
        onItemCreated={(created) => {
          // Sempre pré-seleciona, mesmo que o escopo não case com o tipo
          // atual — o fallback de "seleção atual" do dropdown garante visibilidade.
          setForm((f) => ({ ...f, categoriaId: String(created.id) }));
        }}
        onListChanged={() => {
          // Recarrega a lista no FinanceiroFormModal pra refletir edits/exclusões.
          categoriasService.listCategorias()
            .then(setCategorias)
            .catch(() => {});
        }}
      />

      {/* Gerenciador de Formas de Pagamento — idem */}
      <TaxonomyManagerModal
        open={formaManagerOpen}
        onClose={() => setFormaManagerOpen(false)}
        onList={formasPagamentoService.listFormasPagamento}
        onCreate={formasPagamentoService.createFormaPagamento}
        onUpdate={formasPagamentoService.updateFormaPagamento}
        onDelete={formasPagamentoService.deleteFormaPagamento}
        titulo="Gerenciar Formas de Pagamento"
        itemLabel="forma de pagamento"
        onItemCreated={(created) => {
          setForm((f) => ({ ...f, formaPagamentoId: String(created.id) }));
        }}
        onListChanged={() => {
          formasPagamentoService.listFormasPagamento()
            .then(setFormasPagamento)
            .catch(() => {});
        }}
      />
    </>
  );
}
